<template>
  <div :class="['custom-swiper', { vertical: props.direction === 'vertical' }]">
    <span
      v-show="props.navigation"
      class="swiper-navigation-btn swiper-btn-prev"
      :class="{ hidden: !props.navigation }"
      ref="btnPrev"></span>

    <swiper-container :id="attrs.id ? `swiper-${attrs.id}` : ''" class="swiper-wrapper" init="false" ref="swiper">
      <slot></slot>
    </swiper-container>
    <div v-show="props.scrollbar" class="swiper-scrollbar" :class="{ hidden: !props.scrollbar }" ref="scrollbar"></div>

    <span
      v-show="props.navigation"
      class="swiper-navigation-btn swiper-btn-next"
      :class="{ hidden: !props.navigation }"
      ref="btnNext"></span>
    <div v-show="props.pagination" :class="['pagination', { hidden: !props.pagination }]" ref="pagination"></div>
  </div>
</template>

<script lang="ts" setup>
  import { ref, onMounted, defineProps, defineEmits, reactive, withDefaults, useAttrs } from "vue";
  import { BREAKPOINTS, INJECT_STYLES } from "@/utils/swiper";
  const emit = defineEmits(["updateActiveSlideIndex", "swiperInit"]);
  const swiper = ref();
  const btnNext = ref<HTMLElement>();
  const btnPrev = ref<HTMLElement>();
  const pagination = ref<HTMLElement>();
  const scrollbar = ref<HTMLElement>();
  const attrs = useAttrs();

  const props = withDefaults(
    defineProps<{
      slidesPerView?: number | string;
      navigation?: boolean;
      pagination?: boolean;
      scrollbar?: boolean;
      mousewheel?: boolean;
      direction?: string;
      thumbsSwiper?: string | null;
      spaceBetween?: number | null;
      centeredSlides?: boolean;
      centeredSlidesBounds?: boolean;
      checkActiveIndex?: boolean;
      slideToClickedSlide?: boolean;
      slidesPerGroup?: number;
      type?: string;
      breakpointKey?: string;
      initialSlide?: number;
      loop?: boolean;
      allowTouchMove?: boolean;
      dynamicBullets?: boolean;
      dynamicMainBullets?: number;
      keyboard?: boolean;
      grid?: object;
      freeMode?: { enabled: boolean; sticky: boolean } | boolean;
      customEventNavigationButtonsClick?: boolean;
      speed?: number | null;
    }>(),
    {
      slidesPerView: 6,
      navigation: true,
      pagination: false,
      scrollbar: false,
      mousewheel: false,
      direction: "horizontal",
      thumbsSwiper: null,
      spaceBetween: null,
      centeredSlides: false,
      centeredSlidesBounds: false,
      checkActiveIndex: false,
      slideToClickedSlide: false,
      type: null,
      breakpointKey: null,
      initialSlide: 0,
      loop: false,
      allowTouchMove: true,
      dynamicBullets: false,
      dynamicMainBullets: 6,
      keyboard: false,
      freeMode: false,
      customEventNavigationButtonsClick: false,
      speed: 300
    }
  );

  const state = reactive({
    activeIndex: 0,
    slides: null as [] | null
  });

  onMounted(() => {
    const swiperEl = swiper;

    if (!swiper.value) {
      console.error("ref swiper is undefined in Swiper.vue component");
      return;
    }

    if (!swiper.value.querySelectorAll) {
      return;
    }
    state.slides = Array.from(swiper.value.querySelectorAll(".swiper-slide"));

    const swiperParams = {
      watchOverflow: true,
      watchSlidesProgress: true,
      slidesPerView: props.slidesPerView,
      direction: props.direction,
      spaceBetween: props.spaceBetween,
      centeredSlides: props.centeredSlides,
      centeredSlidesBounds: props.centeredSlidesBounds,
      slideToClickedSlide: props.slideToClickedSlide,
      breakpoints: BREAKPOINTS[props.type || props.breakpointKey],
      initialSlide: props.initialSlide,
      loop: props.loop,
      grid: props.grid,
      slidesPerGroup: props.slidesPerGroup,
      allowTouchMove: props.allowTouchMove,
      freeMode: props.freeMode,
      speed: props.speed,
      injectStyles: [INJECT_STYLES],
      navigation: {
        nextEl: btnNext.value,
        prevEl: btnPrev.value
      },
      pagination: {
        el: pagination.value,
        clickable: true,
        dynamicBullets: props.dynamicBullets,
        dynamicMainBullets: props.dynamicMainBullets
      },
      scrollbar: {
        el: scrollbar.value,
        draggable: true
      },
      ...(props.type?.length
        ? {
            containerModifierClass: `swiper-${props.type}-` // bad variable broken swiper styles
          }
        : {}),
      ...(props.keyboard
        ? {
            keyboard: {
              enabled: true
            }
          }
        : {}),
      ...(props.mousewheel
        ? {
            mousewheel: {
              enabled: true
            }
          }
        : {}),
      ...(props.thumbsSwiper
        ? {
            thumbs: {
              swiper: props.thumbsSwiper
            }
          }
        : {}),
      on: {
        afterInit: (sw) => {
          emit("swiperInit", sw);
        }
      }
    };

    Object.assign(swiperEl.value, swiperParams);

    swiperEl.value?.initialize();

    if (props.checkActiveIndex) {
      swiperEl.value?.swiper.on("slideChange", () => {
        updateActiveSlideIndex();
      });
    }

    if (props.customEventNavigationButtonsClick) {
      const swiperData = swiper.value.swiper;
      swiperData.navigation.disable();

      btnNext.value?.addEventListener("click", () => {
        swiper.value?.swiper.slideTo(swiperData.activeIndex + swiperData.params.slidesPerView);
      });

      btnPrev.value?.addEventListener("click", () => {
        swiper.value?.swiper.slideTo(swiperData.activeIndex - swiperData.params.slidesPerView);
      });
    }
  });

  const updateActiveSlideIndex = () => {
    emit("updateActiveSlideIndex", swiper.value?.swiper.activeIndex);
  };
</script>
